import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo, LogoLockup, Wordmark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { errMessage } from "@/lib/api";
import { ArrowRight } from "lucide-react";

const HERO = "https://images.pexels.com/photos/5749191/pexels-photo-5749191.jpeg";

export default function Auth() {
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const name = form.name.trim();
    const email = form.email.trim();
    const pw = form.password;
    // Explicit, visible validation (no silent browser popups)
    if (mode === "register" && !name) return setError("Please enter your name.");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Please enter a valid email address.");
    if (!pw || pw.length < 6) return setError("Password must be at least 6 characters.");

    setBusy(true);
    try {
      if (mode === "register") {
        await register(name, email, pw);
        toast.success("Welcome to the Haus. Build your HausCard.");
      } else {
        await login(email, pw);
        toast.success("Welcome back.");
      }
      navigate("/profile");
    } catch (err) {
      const msg = errMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ink">
      {/* Left visual */}
      <div className="hidden lg:block relative border-r border-line">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 media-grayscale" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30 grid-paper" />
        <div className="relative z-10 h-full flex flex-col justify-between gap-8 p-12">
          <Link to="/" className="w-fit">
            <Wordmark height={56} />
          </Link>
          <video
            src="/video/promo-v2.mp4"
            poster="/video/promo-poster-v2.jpg"
            autoPlay muted loop playsInline controls
            data-testid="auth-promo-video"
            className="w-full max-w-xl border border-line bg-ink aspect-video object-cover"
          />
          <div>
            <h2 className="font-heading text-5xl font-bold tracking-tighter uppercase text-cream leading-[0.95]"><span className="text-gold">The home</span><br />for indie<br />music makers.</h2>
            <p className="text-ash mt-6 max-w-sm">Three songs. Real people. A private home for writers, artists & producers.</p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 text-cream mb-6">
            <Wordmark height={30} />
          </Link>
          <video
            src="/video/promo-v2.mp4"
            poster="/video/promo-poster-v2.jpg"
            autoPlay muted loop playsInline controls
            data-testid="auth-promo-video-mobile"
            className="lg:hidden w-full border border-line bg-ink aspect-video object-cover mb-8"
          />

          <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{mode === "register" ? "Create your free profile" : "Welcome back"}</span>
          <h1 className="font-heading text-4xl font-bold tracking-tighter uppercase text-cream mt-3 mb-8">
            {mode === "register" ? "Enter the Haus" : "Sign in"}
          </h1>

          <button
            onClick={google}
            data-testid="google-auth-btn"
            className="w-full flex items-center justify-center gap-3 border border-line text-cream font-mono text-xs uppercase tracking-widest py-4 hover:border-gold hover:text-gold transition-colors mb-6"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81Z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-line" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-ash">or with email</span>
            <div className="flex-1 h-px bg-line" />
          </div>

          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {mode === "register" && (
              <input
                data-testid="auth-name-input"
                placeholder="Your name"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-surface border border-line text-cream px-4 py-3.5 focus:outline-none focus:border-gold transition-colors placeholder:text-ash/60"
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email" inputMode="email" autoComplete="email" placeholder="Email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-surface border border-line text-cream px-4 py-3.5 focus:outline-none focus:border-gold transition-colors placeholder:text-ash/60"
            />
            <div>
              <input
                data-testid="auth-password-input"
                type="password" autoComplete={mode === "register" ? "new-password" : "current-password"} placeholder="Password"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-surface border border-line text-cream px-4 py-3.5 focus:outline-none focus:border-gold transition-colors placeholder:text-ash/60"
              />
              {mode === "register" && <p className="font-mono text-[11px] text-ash/70 mt-1.5">At least 6 characters</p>}
            </div>

            {error && (
              <p data-testid="auth-error" className="border border-destructive/50 bg-destructive/10 text-destructive text-sm px-4 py-3">{error}</p>
            )}

            <button
              type="submit" disabled={busy}
              data-testid="auth-submit-btn"
              className="flex items-center justify-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-sm py-4 hover:bg-gold-hi transition-colors disabled:opacity-60"
            >
              {busy ? "…" : mode === "register" ? "Create profile" : "Sign in"}
              {!busy && <ArrowRight size={16} />}
            </button>
          </form>

          <p className="text-ash text-sm mt-8">
            {mode === "register" ? "Already in the Haus? " : "New here? "}
            <button
              data-testid="auth-toggle-mode"
              onClick={() => { setMode(mode === "register" ? "login" : "register"); setError(""); }}
              className="text-gold hover:underline"
            >
              {mode === "register" ? "Sign in" : "Create a free profile"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
